/**
 * Browser Compatibility Detection and Fallback Utilities
 *
 * Provides comprehensive browser feature detection and automatic fallback
 * to legacy portal for unsupported browsers.
 */

import { ENV } from 'config';
import { initialState } from 'features/auth/store';
import { APP_ROUTES } from 'shared/constants';

export interface BrowserCompatResult {
    isCompatible: boolean;
    failedFeatures: string[];
    userAgent: string;
    recommendation: 'continue' | 'warn' | 'redirect';
    compatibilityScore: number; // 0-1 scale
}

interface FeatureDetectionResult {
    name: string;
    supported: boolean;
    critical: boolean;
}

/**
 * Detect critical JavaScript features required by React 18 and modern libraries
 */
function detectJavaScriptFeatures(): FeatureDetectionResult[] {
    return [
        {
            name: 'Promise',
            supported: typeof Promise !== 'undefined',
            critical: true
        },
        {
            name: 'WeakMap',
            supported: typeof WeakMap !== 'undefined',
            critical: true
        },
        {
            name: 'Proxy',
            supported: typeof Proxy !== 'undefined',
            critical: true
        },
        {
            name: 'Symbol',
            supported: typeof Symbol !== 'undefined',
            critical: true
        },
        {
            name: 'Map',
            supported: typeof Map !== 'undefined',
            critical: false
        },
        {
            name: 'Set',
            supported: typeof Set !== 'undefined',
            critical: false
        }
    ];
}

/**
 * Detect critical CSS features required by MUI and modern styling
 */
function detectCSSFeatures(): FeatureDetectionResult[] {
    const cssSupports = (property: string, value: string): boolean => {
        if (typeof CSS === 'undefined' || !CSS.supports) {
            return false;
        }

        try {
            return CSS.supports(property, value);
        } catch {
            return false;
        }
    };

    return [
        {
            name: 'CSS Variables',
            supported: cssSupports('--test', '0'),
            critical: true
        },
        {
            name: 'Flexbox Gap',
            supported: cssSupports('gap', '1rem') && cssSupports('display', 'flex'),
            critical: false
        },
        {
            name: 'CSS Grid',
            supported: cssSupports('display', 'grid'),
            critical: false
        },
        {
            name: 'Object Fit',
            supported: cssSupports('object-fit', 'cover'),
            critical: false
        }
    ];
}

/**
 * Detect known problematic browsers via User-Agent parsing
 */
function detectProblematicBrowser(): {
    isProblematic: boolean;
    browserName: string;
    reason: string;
    } {
    const ua = navigator.userAgent;

    // IE 11 detection
    if (/MSIE|Trident/.test(ua)) {
        return {
            isProblematic: true,
            browserName: 'Internet Explorer 11',
            reason: 'IE11 not supported by MUI v7 and React 18'
        };
    }

    // Old Edge (EdgeHTML) detection - Edge < 79
    const edgeMatch = /Edge\/([0-9]+)/.exec(ua);

    if (edgeMatch && parseInt(edgeMatch[1]) < 79) {
        return {
            isProblematic: true,
            browserName: `Edge Legacy ${ edgeMatch[1] }`,
            reason: 'Pre-Chromium Edge lacks modern features'
        };
    }

    // Old Android Browser (< 5.0)
    const androidMatch = /Android\s([0-9.]+)/.exec(ua);

    if (androidMatch && parseFloat(androidMatch[1]) < 5.0) {
        return {
            isProblematic: true,
            browserName: `Android Browser ${ androidMatch[1] }`,
            reason: 'Pre-Chrome Android lacks ES6 support'
        };
    }

    // Old Safari (< 12.1)
    const safariMatch = /Version\/([0-9.]+).*Safari/.exec(ua);

    if (safariMatch && parseFloat(safariMatch[1]) < 12.1) {
        return {
            isProblematic: true,
            browserName: `Safari ${ safariMatch[1] }`,
            reason: 'Safari < 12.1 lacks modern CSS and JS features'
        };
    }

    // Old Chrome (< 64)
    const chromeMatch = /Chrome\/([0-9]+)/.exec(ua);

    if (chromeMatch && parseInt(chromeMatch[1]) < 64 && !/Edge/.test(ua)) {
        return {
            isProblematic: true,
            browserName: `Chrome ${ chromeMatch[1] }`,
            reason: 'Chrome < 64 lacks modern features'
        };
    }

    // Old Firefox (< 60)
    const firefoxMatch = /Firefox\/([0-9]+)/.exec(ua);

    if (firefoxMatch && parseInt(firefoxMatch[1]) < 60) {
        return {
            isProblematic: true,
            browserName: `Firefox ${ firefoxMatch[1] }`,
            reason: 'Firefox < 60 lacks modern features'
        };
    }

    return {
        isProblematic: false,
        browserName: 'Unknown',
        reason: ''
    };
}

/**
 * Calculate compatibility score based on feature support
 */
function calculateCompatibilityScore(
    jsFeatures: FeatureDetectionResult[],
    cssFeatures: FeatureDetectionResult[]
): number {
    const allFeatures = [ ...jsFeatures, ...cssFeatures ];
    const criticalFeatures = allFeatures.filter((f) => f.critical);
    const nonCriticalFeatures = allFeatures.filter((f) => !f.critical);

    const criticalSupported = criticalFeatures.filter((f) => f.supported).length;
    const criticalTotal = criticalFeatures.length;
    const criticalScore = criticalTotal > 0 ? criticalSupported / criticalTotal : 1;

    const nonCriticalSupported = nonCriticalFeatures.filter((f) => f.supported).length;
    const nonCriticalTotal = nonCriticalFeatures.length;
    const nonCriticalScore = nonCriticalTotal > 0 ? nonCriticalSupported / nonCriticalTotal : 1;

    // Weight: 80% critical, 20% non-critical
    return criticalScore * 0.8 + nonCriticalScore * 0.2;
}

/**
 * Comprehensive browser compatibility detection
 */
export function detectBrowserCompatibility(): BrowserCompatResult {
    const jsFeatures = detectJavaScriptFeatures();
    const cssFeatures = detectCSSFeatures();
    const problematicBrowser = detectProblematicBrowser();

    const failedFeatures: string[] = [];

    // Collect failed features
    [ ...jsFeatures, ...cssFeatures ].forEach((feature) => {
        if (!feature.supported) {
            failedFeatures.push(feature.name);
        }
    });

    const compatibilityScore = calculateCompatibilityScore(jsFeatures, cssFeatures);

    // Determine if browser is compatible
    const hasCriticalFailures = [ ...jsFeatures, ...cssFeatures ].some(
        (f) => f.critical && !f.supported
    );

    const isCompatible = !hasCriticalFailures && !problematicBrowser.isProblematic;

    // Determine recommendation
    let recommendation: 'continue' | 'warn' | 'redirect' = 'continue';

    if (problematicBrowser.isProblematic || hasCriticalFailures) {
        recommendation = 'redirect';
    } else if (compatibilityScore < 0.9) {
        recommendation = 'warn';
    }

    return {
        isCompatible,
        failedFeatures,
        userAgent: navigator.userAgent,
        recommendation,
        compatibilityScore
    };
}

const OLD_PORTAL_SUPPORTED_URLS = [
    '/candidate/offers/',
    '/availability_for_period/'
];

/**
 * Generate URL for old portal with current path and authentication preserved
 */
export function generateOldPortalUrl(reason?: string): string {
    const oldPortalBase = ENV.OLD_PORTAL_URL;
    const currentPath = window.location.pathname.replace(APP_ROUTES.basename, '');
    const currentSearch = window.location.search;
    const newPath = OLD_PORTAL_SUPPORTED_URLS.includes(currentPath)
        ? currentPath
        : OLD_PORTAL_SUPPORTED_URLS[0].slice(0, -1);

    let redirectUrl = oldPortalBase + newPath;

    const searchParams = new URLSearchParams(currentSearch);
    const candidateToken = initialState.accessToken?.token || initialState.accessToken?.oldAvailabilityToken;
    
    if (candidateToken) {
        searchParams.set('candidateToken', candidateToken);
    }

    if (reason) {
        searchParams.set('fallback', reason);
    }

    const searchString = searchParams.toString();

    if (searchString) {
        redirectUrl += (currentPath.includes('?') ? '&' : '?') + searchString;
    }

    return redirectUrl;
}

export function redirectToOldPortal(reason = 'unknown'): void {
    const url = generateOldPortalUrl(reason);

    // eslint-disable-next-line no-console
    console.warn(`Redirecting to old portal due to: ${ reason }`);
    localStorage.setItem('redirectedFromNewPortal', reason);

    // Use replace to prevent back button issues
    window.location.replace(url);
}

export function isBrowserRelatedError(error: Error): boolean {
    const errorMessage = error.message?.toLowerCase() || '';

    const browserRelatedKeywords = [
        'proxy',
        'weakmap',
        'promise',
        'symbol',
        'cannot read property',
        'is not a function',
        'unexpected token',
        'not supported',
        'undefined is not an object',
        'null is not an object'
    ];

    return browserRelatedKeywords.some((keyword) => errorMessage.includes(keyword));
}
